import { apiClient } from './client';

export type MiniAppVisibility = 'PRIVATE' | 'UNLISTED' | 'PUBLIC';
export type MiniAppLaunchScope = 'STANDALONE' | 'CHANNEL' | 'DM' | 'BOT' | 'PROFILE';

export interface PublicMiniApp {
  id: string;
  ownerId: string;
  botId: string | null;
  slug: string;
  displayName: string;
  shortDescription: string | null;
  description: string | null;
  iconUrl: string | null;
  url: string;
  allowedOrigins: string[];
  capabilities: string[];
  visibility: MiniAppVisibility;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MiniAppLaunchResult {
  token: string;
  sessionId: string;
  expiresAt: string;
  miniApp: PublicMiniApp;
  launchUrl: string;
}

export interface CreateMiniAppInput {
  slug: string;
  displayName: string;
  url: string;
  allowedOrigins: string[];
  capabilities: string[];
  shortDescription?: string;
  description?: string;
  iconUrl?: string | null;
  visibility?: MiniAppVisibility;
  botId?: string | null;
}

export interface UpdateMiniAppInput {
  displayName?: string;
  url?: string;
  allowedOrigins?: string[];
  capabilities?: string[];
  shortDescription?: string | null;
  description?: string | null;
  iconUrl?: string | null;
  visibility?: MiniAppVisibility;
  isEnabled?: boolean;
  botId?: string | null;
}

export interface LaunchContext {
  channelId?: string;
  peerUserId?: string;
  botId?: string;
  messageId?: string;
  dmId?: string;
}

export const miniAppsApi = {
  async listOwned(): Promise<PublicMiniApp[]> {
    const r = await apiClient.get<PublicMiniApp[]>('/api/mini-apps/owned');
    return r.data;
  },
  async getOwned(id: string): Promise<PublicMiniApp> {
    const r = await apiClient.get<PublicMiniApp>(`/api/mini-apps/owned/${id}`);
    return r.data;
  },
  async create(input: CreateMiniAppInput): Promise<PublicMiniApp & { signingSecret: string }> {
    const r = await apiClient.post<PublicMiniApp & { signingSecret: string }>('/api/mini-apps/owned', input);
    return r.data;
  },
  async update(id: string, input: UpdateMiniAppInput): Promise<PublicMiniApp> {
    const r = await apiClient.patch<PublicMiniApp>(`/api/mini-apps/owned/${id}`, input);
    return r.data;
  },
  async rotateSecret(id: string): Promise<{ signingSecret: string }> {
    const r = await apiClient.post<{ signingSecret: string }>(`/api/mini-apps/owned/${id}/rotate-secret`);
    return r.data;
  },
  async remove(id: string): Promise<void> {
    await apiClient.delete(`/api/mini-apps/owned/${id}`);
  },

  async listDirectory(): Promise<PublicMiniApp[]> {
    const r = await apiClient.get<PublicMiniApp[]>('/api/mini-apps');
    return r.data;
  },
  async getPublic(slugOrId: string): Promise<PublicMiniApp> {
    const r = await apiClient.get<PublicMiniApp>(`/api/mini-apps/${slugOrId}`);
    return r.data;
  },

  async launch(id: string, scope: MiniAppLaunchScope, context: LaunchContext = {}): Promise<MiniAppLaunchResult> {
    const r = await apiClient.post<MiniAppLaunchResult>(`/api/mini-apps/${id}/launch`, { scope, context });
    return r.data;
  },
};
